import React, { useEffect, useState } from "react";
import slugify from "react-slugify";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Switch,
} from "@mui/material";

const emptyPlan = {
  plan_name: "",
  price_per_seat: 0,
  scan_limit_per_day: 0,
  code_size_limit: 0,
  ai_pre_analysis_limit: 0,
  immutable: false,
  tool_access: {},
  platform_access_level: "",
  is_published: false,
  status: "",
  value: 1,
  features: {
    csv_report: false,
    pdf_report: false,
    code_debugging: false,
    ai_feature: false,
    ai_auto_fix: false,
  },
};

const PlansManagement = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [planForm, setPlanForm] = useState(emptyPlan);
  const [tools, setTools] = useState({
    buildTool: [],
    staticAnalysisTools: [],
    unitTestingTools: [],
    memoryTestingTools: [],
    cybersecurityAnalysisTools: [],
    fossComplianceTools: []
  });
  const [selectedTools, setSelectedTools] = useState({});
  const token = localStorage.getItem("access_token");

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/plan/all`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch plans");
      const data = await response.json();
      setPlans(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleDelete = async (planId) => {
    if (!window.confirm("Delete this plan?")) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/plan?plan_id=${planId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) throw new Error("Delete failed");
      fetchPlans(); // refresh
    } catch (err) {
      alert("Error deleting plan: " + err.message);
    }
  };

  const handleEdit = (plan) => {
    setPlanForm({ ...plan }); // Deep copy
    setSelectedTools(plan.tool_access || {});
    setEditMode(true);
    setOpenDialog(true);
  };

  const handleCreateNew = () => {
    setPlanForm({ ...emptyPlan });
    setEditMode(false);
    setOpenDialog(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.startsWith("features.")) {
      const featureKey = name.split(".")[1];
      setPlanForm((prev) => ({
        ...prev,
        features: {
          ...prev.features,
          [featureKey]: checked,
        },
      }));
    } else {
      let updatedValue = value;

      if (name === "status") {
        updatedValue = slugify(value);
      } else if (type === "number") {
        updatedValue = value === "" ? "" : Number(value).toString();
      }

      setPlanForm((prev) => ({
        ...prev,
        [name]: updatedValue,
      }));
    }
  };

  const handleSubmit = async () => {
    const method = editMode ? "PUT" : "POST";
    const planPayload = { ...planForm };
    planPayload.tool_access = selectedTools;
    if (!editMode) {
      // Don't include plan_id on creation; backend will assign UUID
      delete planPayload.plan_id;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/plan`,
        {
          method,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(planPayload),
        }
      );

      if (!response.ok) throw new Error("Failed to save plan");
      await response.json();
      fetchPlans();
      setOpenDialog(false);
    } catch (err) {
      setError(err.message);
    }
  };
  useEffect(() => {
    fetch(
      `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/tools`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to fetch tools");
        }
        return response.json();
      })
      .then((data) => {
        const buildTool = data.filter((tool) => tool.toolType === "buildTool");
        const staticAnalysisTools = data.filter(
          (tool) => tool.toolType === "staticAnalysisTool"
        );
        const unitTestingTools = data.filter(
          (tool) => tool.toolType === "unitTestingTool"
        );
        const memoryTestingTools = data.filter(
          (tool) => tool.toolType === "memoryTestingTool"
        );
        const cybersecurityAnalysisTools = data.filter(
          (tool) => tool.toolType === "cyberSecurityAnalysisTool"
        );
        const fossComplianceTools = data.filter(
          (tool) => tool.toolType === "fossComplianceTool"
        );

        setTools({
          buildTool,
          staticAnalysisTools,
          unitTestingTools,
          memoryTestingTools,
          cybersecurityAnalysisTools,
          fossComplianceTools
        });
      })
      .catch((err) => {
        console.error("Error fetching tools:", err);
        setError("Error loading tools. Please try again later.");
      });
  }, [token]);
  const handleToolToggle = (category, toolId) => {
    const tool = tools[category].find((t) => t.tool_id === toolId);
    if (!tool) return;

    const toolKey = tool.toolKey;

    setSelectedTools((prev) => {
      const updatedCategory = { ...(prev[category] || {}) };

      if (toolKey in updatedCategory) {
        delete updatedCategory[toolKey];
      } else {
        updatedCategory[toolKey] = {
          tool_id: tool.tool_id,
          immutable: false,
        };
      }

      return {
        ...prev,
        [category]: updatedCategory,
      };
    });
  };

  const handleImmutableToggle = (category, toolKey) => {
    setSelectedTools((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [toolKey]: {
          ...prev[category][toolKey],
          immutable: !prev[category][toolKey]?.immutable,
        },
      },
    }));
  };

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Plans Management
      </Typography>
      <Button variant="contained" onClick={handleCreateNew} sx={{ mb: 2 }}>
        Create New Plan
      </Button>
      <Table>
        <TableHead>
          <TableRow>
            {/* <TableCell>Plan ID</TableCell> */}
            <TableCell>Plan</TableCell>
            <TableCell>Price/Seat</TableCell>
            <TableCell>Scan Limit</TableCell>
            <TableCell>Code Size</TableCell>
            <TableCell>Ai Scan Limit</TableCell>
            <TableCell>Access Level</TableCell>
            <TableCell>Published</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {plans.map((plan) => (
            <TableRow key={plan.plan_id}>
              {/* <TableCell>{plan.plan_id}</TableCell> */}
              <TableCell>{plan.plan_name}</TableCell>
              <TableCell>{plan.price_per_seat}</TableCell>
              <TableCell>{plan.scan_limit_per_day}</TableCell>
              <TableCell>{plan.code_size_limit}</TableCell>
              <TableCell>{plan.ai_pre_analysis_limit}</TableCell>
              <TableCell>{plan.platform_access_level}</TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  sx={{
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    color: "white",
                    backgroundColor: plan.is_published ? "green" : "grey",
                    display: "inline-block",
                  }}
                >
                  {plan.is_published ? "Published" : "Unpublished"}
                </Typography>
              </TableCell>
              <TableCell>{plan.status}</TableCell>
              <TableCell>
                <Button onClick={() => handleEdit(plan)}>Edit</Button>
                <Button
                  color="error"
                  onClick={() => handleDelete(plan.plan_id)}
                >
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {openDialog && (
        <Box
          sx={{
            mt: 4,
            p: 3,
            border: "1px solid #ccc",
            borderRadius: 2,
            backgroundColor: "#fafafa",
          }}
        >
          <Typography variant="h6" gutterBottom>
            {editMode ? "Edit Plan" : "Create New Plan"}
          </Typography>
          {editMode && (
            <TextField
              fullWidth
              margin="dense"
              label="Plan ID"
              name="plan_id"
              value={planForm.plan_id}
              disabled
              hidden
            />
          )}
          <TextField
            fullWidth
            margin="dense"
            label="Plan Name"
            name="plan_name"
            value={planForm.plan_name}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            type="number"
            margin="dense"
            label="Price Per Seat"
            name="price_per_seat"
            value={planForm.price_per_seat}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            type="number"
            margin="dense"
            label="Scan Limit Per Day"
            name="scan_limit_per_day"
            value={planForm.scan_limit_per_day}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            type="number"
            margin="dense"
            label="Code Size Limit"
            name="code_size_limit"
            value={planForm.code_size_limit}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            type="number"
            margin="dense"
            label="Ai Pre Analysis Limit"
            name="ai_pre_analysis_limit"
            value={planForm.ai_pre_analysis_limit}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            type="number"
            margin="dense"
            label="value"
            name="value"
            value={planForm.value}
            onChange={handleChange}
          />
          {/* Immutable Switch
          <FormControl fullWidth margin="dense">
            <Typography variant="body2" sx={{ mb: 1 }}>
              Immutable
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={planForm.immutable}
                  onChange={(e) =>
                    setPlanForm((prev) => ({
                      ...prev,
                      immutable: e.target.checked,
                    }))
                  }
                  color="primary"
                />
              }
              label={planForm.immutable ? "True" : "False"}
            />
          </FormControl> */}

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Tool Access:
            </Typography>
            {Object.entries(tools).map(([category, toolList]) => {
              const selectedToolNames = Object.keys(
                selectedTools[category] || {}
              );
              const allToolNames = toolList.map((tool) => tool.toolName);

              return (
                <Box key={category} sx={{ ml: 2, mt: 1 }}>
                  {/* Select All Checkbox */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          selectedToolNames.length === allToolNames.length &&
                          allToolNames.length > 0
                        }
                        indeterminate={
                          selectedToolNames.length > 0 &&
                          selectedToolNames.length < allToolNames.length
                        }
                        onChange={() => {
                          setSelectedTools((prev) => {
                            const allSelected =
                              selectedToolNames.length === toolList.length;

                            const newCategorySelection = allSelected
                              ? {}
                              : Object.fromEntries(
                                  toolList.map((tool) => [
                                    tool.toolKey,
                                    { tool_id: tool.tool_id, immutable: false },
                                  ])
                                );

                            return {
                              ...prev,
                              [category]: newCategorySelection,
                            };
                          });
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontWeight: "bold" }}>
                        {category.replace(/([A-Z])/g, " $1")}
                      </Typography>
                    }
                  />

                  {/* List of tools */}
                  <Box sx={{ ml: 4 }}>
                    {toolList.map((tool) => {
                      const toolKey = tool.toolKey;
                      const isSelected = !!selectedTools[category]?.[toolKey];

                      return (
                        <Box
                          key={tool.tool_id}
                          display="flex"
                          alignItems="center"
                          gap={2}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={isSelected}
                                onChange={() =>
                                  handleToolToggle(category, tool.tool_id)
                                }
                              />
                            }
                            label={tool.toolName}
                          />
                          {isSelected && (
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={
                                    selectedTools[category][toolKey]
                                      ?.immutable || false
                                  }
                                  onChange={() =>
                                    handleImmutableToggle(category, toolKey)
                                  }
                                  color="primary"
                                />
                              }
                              label="Immutable"
                            />
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* PlatfromAcess Level Select */}
          <FormControl fullWidth margin="dense">
            <InputLabel>Platform Access Level</InputLabel>
            <Select
              name="platform_access_level"
              value={planForm.platform_access_level}
              label="Platform Access Level"
              onChange={handleChange}
            >
              <MenuItem value="Limited">Limited</MenuItem>
              <MenuItem value="Enhanced">Enhanced</MenuItem>
              <MenuItem value="Full">Full</MenuItem>
            </Select>
          </FormControl>
          {/* Published Switch */}
          <FormControl fullWidth margin="dense">
            <FormControlLabel
              control={
                <Switch
                  checked={
                    planForm.is_published === true ||
                    planForm.is_published === "true"
                  }
                  onChange={(e) =>
                    setPlanForm((prev) => ({
                      ...prev,
                      is_published: e.target.checked,
                    }))
                  }
                  color="primary"
                />
              }
              label={
                planForm.is_published === true ||
                planForm.is_published === "true"
                  ? "Published"
                  : "Unpublished"
              }
            />
          </FormControl>

          {/* Status Select */}
          <FormControl fullWidth margin="dense">
            <InputLabel>Status</InputLabel>
            <Select
              name="status"
              value={planForm.status}
              label="Status"
              onChange={handleChange}
            >
              {["Active", "Coming Soon"].map((label) => (
                <MenuItem key={label} value={slugify(label)}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1">Features:</Typography>
            {Object.keys(planForm.features).map((key) => (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox
                    name={`features.${key}`}
                    checked={planForm.features[key]}
                    onChange={handleChange}
                  />
                }
                label={key.replace(/_/g, " ")}
              />
            ))}
          </Box>
          <Box sx={{ mt: 3 }}>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit}>
              {editMode ? "Update" : "Create"}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PlansManagement;
